// This file creates a custom datafeed adapter for TradingView
// to work with real market data

class Datafeed {
  constructor(symbol = null, decimals = 4, marketType = null) {
    this.symbol = symbol; // Keep original symbol for data handling
    this.marketType = marketType || 'forex'; // Default to forex if no type provided
    
    // Store the original decimals value for reference
    this.decimals = decimals;
    
    // Calculate pricescale
    this.calculatePricescale();
    
    // Initialize data storage
    this.data = null;
    this.callbacks = {};
    this.subscribers = {};
    this.currentSymbol = null;
    this.lastBar = null; // Store the last bar for real-time updates
  }
  
  // Format symbol to extract the actual instrument name after the last period
  // Example: 0000001.FX.AUDCAD -> AUDCAD
  formatSymbol(symbol) {
    if (!symbol) return null;
    
    // Split by period and take the last part
    const parts = symbol.split('.');
    const formattedSymbol = parts.length > 1 ? parts[parts.length - 1] : symbol;
    
    return formattedSymbol;
  }
  
  // Calculate pricescale based on decimals
  calculatePricescale() {
    this.pricescale = 100000; // Default (5 decimals)
    
    if (this.decimals !== null && this.decimals !== undefined) {
      if (typeof this.decimals === 'number') {
        // Handle numeric decimals
        this.pricescale = Math.pow(10, this.decimals);
      } else if (typeof this.decimals === 'string') {
        // Handle string formatted as a number (e.g. "4", "5")
        const parsedDecimals = parseInt(this.decimals, 10);
        if (!isNaN(parsedDecimals)) {
          this.pricescale = Math.pow(10, parsedDecimals);
        }
        // Handle string formatted as a tick size (e.g. "0.0001") 
        else if (this.decimals.match(/^0\.\d+$/)) {
          const decimalPlaces = this.decimals.split('.')[1].length;
          this.pricescale = Math.pow(10, decimalPlaces);
        }
      }
    }
  }

  // Clear all data when switching instruments
  clearData() {
    this.data = null;
    this.currentSymbol = null;
    this.subscribers = {}; // Clear subscribers too to prevent stale updates
    this.lastBar = null; // Clear last bar too
  }

  // Update the data when new data is received
  updateData(data) {
    if (!Array.isArray(data) || data.length === 0) {
      return;
    }

    // Store the data
    this.data = [...data];
    
    // Store the last bar for real-time updates
    if (data.length > 0) {
      this.lastBar = data[data.length - 1];
    }
  }

  // Convert our data format to TradingView bar format
  formatBar(bar) {
    if (!bar) {
      console.error('[Datafeed] Attempted to format undefined bar');
      return null;
    }
    
    return {
      time: bar.time * 1000, // Convert to milliseconds
      open: parseFloat(bar.open),
      high: parseFloat(bar.high),
      low: parseFloat(bar.low),
      close: parseFloat(bar.close),
      volume: parseFloat(bar.volume || 0)
    };
  }

  // Handle real-time price update and update the last bar
  updateRealtimeBar(price) {
    // Basic validation of inputs
    if (price === null || price === undefined || isNaN(parseFloat(price))) {
      return false;
    }
    
    // Ensure we have a valid numeric price
    const numericPrice = parseFloat(price);
    
    if (!this.lastBar) {
      // If we have a symbol but no last bar, create a new one with the current time
      if (this.currentSymbol) {
        const currentTime = Math.floor(Date.now() / 1000);
        
        // Create a placeholder last bar
        this.lastBar = {
          time: currentTime,
          open: numericPrice,
          high: numericPrice,
          low: numericPrice,
          close: numericPrice,
          volume: 0
        };
      } else {
        return false;
      }
    }
    
    if (!this.currentSymbol) {
      return false;
    }

    // Get current time in seconds
    const currentTime = Math.floor(Date.now() / 1000);
    
    // If we have no subscribers, there's no need to process the update
    if (Object.keys(this.subscribers).length === 0) {
      return false;
    }
    
    try {
      // Update subscribers with the new price
      Object.keys(this.subscribers).forEach(subscriberUID => {
        const subscriberInfo = this.subscribers[subscriberUID];
        if (!subscriberInfo || !subscriberInfo.callback) {
          return;
        }
        
        const resolution = subscriberInfo.resolution;
        const lastBarTime = this.lastBar.time;
        
        // Get bar duration in seconds
        const barDuration = this.getBarDurationSeconds(resolution);
        
        // Calculate the current bar's start time
        // This aligns the bar start time to the proper interval boundary
        const currentBarStartTime = Math.floor(currentTime / barDuration) * barDuration;
        
        // Check if we need to create a new bar
        // A new bar is needed if:
        // 1. The current bar start time is greater than the last bar time (we're in a new interval)
        // 2. AND the current time is at least at the start of this new bar
        const needsNewBar = currentBarStartTime > lastBarTime && currentTime >= currentBarStartTime;
        
        if (needsNewBar) {
          // Ensure the new bar time is greater than the last bar time to prevent time order violations
          if (currentBarStartTime <= lastBarTime) {
            console.warn('[Datafeed] Time order issue detected! Skipping bar creation.');
            return;
          }
          
          // Create a new bar
          const newBar = {
            time: currentBarStartTime * 1000, // in milliseconds for TradingView
            open: numericPrice,
            high: numericPrice,
            low: numericPrice,
            close: numericPrice,
            volume: 0
          };
          
          // Store the new bar as lastBar in raw format
          this.lastBar = {
            time: currentBarStartTime,
            open: numericPrice,
            high: numericPrice,
            low: numericPrice,
            close: numericPrice,
            volume: 0
          };
          
          // Send to subscriber
          try {
            subscriberInfo.callback(newBar);
          } catch (error) {
            console.error(`[Datafeed] Error sending new bar to subscriber:`, error);
          }
        } else {
          // Update the existing bar
          try {
            // Create an updated bar with the new close price
            const updatedBar = {
              time: this.lastBar.time * 1000, // Convert to milliseconds for TradingView
              open: parseFloat(this.lastBar.open),
              high: Math.max(parseFloat(this.lastBar.high), numericPrice),
              low: Math.min(parseFloat(this.lastBar.low), numericPrice),
              close: numericPrice,
              volume: parseFloat(this.lastBar.volume || 0)
            };
            
            // Update the lastBar data
            this.lastBar.high = updatedBar.high;
            this.lastBar.low = updatedBar.low;
            this.lastBar.close = numericPrice;
            
            // Call subscriber's callback with the updated bar
            subscriberInfo.callback(updatedBar);
          } catch (error) {
            console.error(`[Datafeed] Error updating bar:`, error);
          }
        }
      });
      
      return true;
    } catch (error) {
      console.error('[Datafeed] Error processing real-time price update:', error);
      return false;
    }
  }
  
  // Helper function to convert resolution to seconds
  getBarDurationSeconds(resolution) {
    // Handle numeric resolutions (minutes)
    if (!isNaN(parseInt(resolution))) {
      return parseInt(resolution) * 60; // Convert minutes to seconds
    }
    
    // Handle special resolutions
    switch (resolution) {
      case '1D':
      case 'D':
        return 24 * 60 * 60; // 1 day in seconds
      case '1W':
      case 'W':
        return 7 * 24 * 60 * 60; // 1 week in seconds
      case '1M':
      case 'M':
        return 30 * 24 * 60 * 60; // Approximately 1 month in seconds
      default:
        return 60; // Default to 1 minute
    }
  }

  // Called once when the chart is initialized
  onReady(callback) {
    // Configure the datafeed with your requirements
    setTimeout(() => callback({
      supports_search: false,
      supports_group_request: false,
      supported_resolutions: ['1', '5', '15', '30', '60', '1D', '1W', '1M'],
      supports_marks: false,
      supports_timescale_marks: false
    }), 0);
  }

  // Called when a chart needs to resolve a symbol
  resolveSymbol(symbolName, onSymbolResolvedCallback, onResolveErrorCallback) {
    // Format the symbol name for display only
    const formattedSymbol = this.formatSymbol(symbolName);
    
    // Update current symbol (keep original for data handling)
    this.currentSymbol = symbolName;
    
    // Simulate a small delay to mimic network request
    setTimeout(() => {
      const symbolInfo = {
        name: symbolName, // Keep original name for data lookup
        description: formattedSymbol, // Use formatted name for display
        type: this.marketType,
        session: '24x7',
        timezone: 'Etc/UTC',
        ticker: symbolName, // Keep original for data lookup
        minmov: 1,
        pricescale: this.pricescale,
        has_intraday: true,
        intraday_multipliers: ['1', '5', '15', '30', '60'],
        supported_resolutions: ['1', '5', '15', '30', '60', '1D', '1W', '1M'],
        volume_precision: 8,
        data_status: 'streaming', // Indicate streaming data for real-time updates
        has_empty_bars: true, // Allow empty bars to be created
        has_daily: true, // Support daily resolution
        has_weekly_and_monthly: true, // Support weekly and monthly resolutions
        visible_plots_set: 'ohlcv', // Show OHLC and volume by default
        // Add a display property with the formatted symbol
        base_name: [formattedSymbol], // This appears in the legend
        full_name: formattedSymbol, // This appears in chart title
      };
      
      onSymbolResolvedCallback(symbolInfo);
    }, 0);
  }

  // Called when chart needs data for a particular time range
  getBars(symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) {
    const { from, to, firstDataRequest } = periodParams;
    
    if (!this.data || this.data.length === 0) {
      onHistoryCallback([], { noData: true });
      return;
    }
    
    // Filter data points that are within the requested time range
    const bars = this.data
      .filter(bar => {
        const barTime = bar.time;
        return barTime >= from && barTime <= to;
      })
      .map(bar => this.formatBar(bar));
    
    if (bars.length === 0) {
      onHistoryCallback([], { noData: true });
      return;
    }
    
    onHistoryCallback(bars);
  }

  // Real-time updates implementation
  subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID) {
    // Store the callback function for use when real-time updates are received
    this.subscribers[subscriberUID] = {
      symbolInfo,
      resolution,
      callback: onRealtimeCallback,
      lastBar: this.lastBar // Store a reference to the last bar
    };
    
    return subscriberUID;
  }

  unsubscribeBars(subscriberUID) {
    // Remove the subscriber
    if (this.subscribers[subscriberUID]) {
      delete this.subscribers[subscriberUID];
    }
  }
}

export default Datafeed; 